import React, { useEffect, useState } from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Button,
    Input,
    FormControl,
    FormLabel,
    useToast,
    Spinner,
} from '@chakra-ui/react';
import { useMsal } from '@azure/msal-react';
import fetchWrapper from '../lib/fetch-wrapper';
import { url } from '../lib/constants';

const AppManager = () => {
    const { instance, accounts } = useMsal();
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const toast = useToast();

    useEffect(() => {
        fetchApps();
    }, []);

    const fetchApps = async () => {
        setLoading(true);
        try {
            const fw = new fetchWrapper();
            const res = await fw.getApps(`${url}/api/apps`);
            setApps(res.data);
        } catch (error: any) {
            console.error("Error fetching apps:", error);
            toast({
                title: "Error fetching apps",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateApp = async () => {
        if (!name) {
            toast({
                title: "App name is required",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        try {
            const fw = new fetchWrapper();
            const res = await fw.createApp(`${url}/api/apps`, name);
            toast({
                title: "App created successfully",
                description: `App ID: ${res.data.AppId}`,
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            fetchApps();
        } catch (error: any) {
            console.error("Error creating app:", error);
            toast({
                title: "Error creating app",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleDeleteApp = async (appId: string) => {
        if (!window.confirm("Are you sure you want to delete this app?")) return;
        try {
            const fw = new fetchWrapper();
            await fw.deleteApp(`${url}/api/apps`, appId);
            toast({
                title: "App deleted successfully",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            fetchApps();
        } catch (error: any) {
            console.error("Error deleting app:", error);
            toast({
                title: "Error deleting app",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleShowSecret = async (appId: string) => {
        try {
            const fw = new fetchWrapper();
            const res = await fw.getAppSecret(`${url}/api/apps`, appId);
            alert(`App Secret: ${res.data.AppSecret}`);
        } catch (error: any) {
            console.error("Error fetching app secret:", error);
            toast({
                title: "Error fetching app secret",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    if (loading) {
        return (
            <Box textAlign="center" mt={4}>
                <Spinner size="xl" />
            </Box>
        );
    }

    return (
        <Box mt={4} height="100%">
            <FormControl mb={4}>
                <FormLabel>App Name</FormLabel>
                <Input
                    placeholder="Enter app name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
                <Button mt={2} variant="brand" onClick={handleCreateApp}>
                    Create App
                </Button>
            </FormControl>

            <Table variant="striped" colorScheme="gray">
                <Thead>
                    <Tr>
                        <Th>App ID</Th>
                        <Th>Name</Th>
                        <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {apps.map((app: any) => (
                        <Tr key={app.AppId}>
                            <Td>{app.AppId}</Td>
                            <Td>{app.Name}</Td>
                            <Td>
                                <Button
                                    variant="brand"
                                    onClick={() => handleShowSecret(app.AppId)}
                                >
                                    Show Secret
                                </Button>
                                <Button
                                    ml={2}
                                    variant="cancel"
                                    onClick={() => handleDeleteApp(app.AppId)}
                                >
                                    Delete
                                </Button>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </Box>
    );
};

export default AppManager;
